import { Component, signal } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ValidatorFn, ValidationErrors } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Alert, AlertsService, I18NService, UserService } from '@socrates/socrates-shared';

export const confirmPasswordValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
	const password = control.get('password');
	const confirm = control.get('confirmPassword');
	return password && confirm && password.value === confirm.value ? null : { 'passwordMismatch': true };
};

@Component({
	selector: 'password-confirm',
	templateUrl: './passwordConfirm.component.html',
	standalone: false,
})
export class PasswordConfirmComponent {
	private _passwordForm: FormGroup;
	private _submitted = false;
	private _canceling = false;
	private username: string;
	readonly submitting = signal(false);
	private readonly _placeholders: any;
	private _formErrors = {
		password: '',
		confirmPassword: ''
	};

	constructor(
		private alertsService: AlertsService,
		private userService: UserService,
		private route: ActivatedRoute,
		private router: Router,
		private formBuilder: FormBuilder,
	) {
		this.username = this.route.snapshot.queryParams?.username;
		this._passwordForm = this.formBuilder.group(
			{
				resetToken: ['', [Validators.required]],
				password: ['', [Validators.required, Validators.minLength(8), Validators.pattern(/((?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*\W)){8,16}/)]],
				confirmPassword: ['', [Validators.required]]
			},
			{ validator: confirmPasswordValidator }
		);
		this._placeholders = {
			password: I18NService.getMessage('login.passwordReset.placeholders.password'),
			passwordConfirm: I18NService.getMessage('login.passwordReset.placeholders.passwordConfirm'),
			resetToken: I18NService.getMessage('login.passwordReset.placeholders.code'),
			email: I18NService.getMessage('login.passwordReset.placeholders.emailPrompt')
		};
		this.alertsService.clearAlerts();
	}

	async onSubmit(): Promise<void> {
		if (!this._canceling) {
			if (this._passwordForm.valid) {
				this.submitting.set(true);
				try {
					await this.userService.resetPasswordConfirm(
						this.username,
						this._passwordForm.controls['password'].value as string,
						this._passwordForm.controls['resetToken'].value as string
					);
					this.submitting.set(false);
					this.router.navigateByUrl('/dashboard');
				} catch (error: any) {
					this.submitting.set(false);
					this.alertsService.addAlert(new Alert({
						message: error.message,
						alertType: 'error',
						namespace: 'resetPass',
					}));
				}
			} else {
				this._submitted = true;
			}
		}
	}

	onCancel(): void {
		this._canceling = true;
		this.router.navigate(['/login']);
	}

	getMessage(key: string): string {
		return I18NService.getMessage(key);
	}

	get formControls() { return this._passwordForm.controls; }
	get passwordForm(): FormGroup { return this._passwordForm; }
	get submitted(): boolean { return this._submitted; }
	get formErrors(): any { return this._formErrors; }
	get placeholders(): any { return this._placeholders; }
}
